import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { motion } from 'motion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from '@/App';
import { AuthProvider } from '@/contexts/AuthContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoaderScreen } from '@/components/loader/LoaderScreen';
import '@/i18n';
import '@/styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

/**
 * Root shell с лоадером.
 *
 * LoaderScreen рендерится поверх App (z-9900) на старте каждой сессии
 * и держится 5–8 сек. App и AuthProvider монтируются ОДНОВРЕМЕННО с
 * лоадером — сетевые запросы (JWT, /auth/me, /onboarding/status,
 * /oferta/status) параллелятся с показом лоадера, так что к моменту
 * его закрытия данные уже готовы.
 *
 * Crossfade: пока лоадер виден, App-обёртка полностью прозрачна
 * (opacity 0). Когда лоадер начинает exit, App плавно проявляется
 * (opacity 0 → 1 за 750мс с 100мс задержкой). Это синхронизируется с
 * exit-fade лоадера (1100мс: opacity+blur+scale) и даёт плавный
 * переход без резкого «пропадания» картинки в пользу основного UI.
 * Работает одинаково для любого next-экрана: AccessGate / Onboarding
 * / Oferta / Dashboard — они все внутри <App />.
 */
function Root() {
  // appVisible включается раньше loaderDone — по событию
  // 'luna:loader-black'. Это крутит crossfade: лоадер уходит в чёрный,
  // под чёрным App проявляется, затем лоадер прозрачнеет.
  const [appVisible, setAppVisible] = useState(false);
  const [loaderDone, setLoaderDone] = useState(false);

  useEffect(() => {
    const handler = () => setAppVisible(true);
    window.addEventListener('luna:loader-black', handler);
    return () => window.removeEventListener('luna:loader-black', handler);
  }, []);

  // Hard fail-safe: если по любой причине LoaderScreen не диспатчит
  // 'luna:loader-black' (не загрузилась картинка, зависла анимация,
  // сломался звук, старый Android WebView), через 12 секунд
  // форсим appVisible=true и loaderDone=true. Юзер попадёт в UI
  // даже если loader завис — это особенно критично на «вёдрах».
  useEffect(() => {
    const forceAppVisible = window.setTimeout(() => setAppVisible(true), 12_000);
    const forceLoaderDone = window.setTimeout(() => {
      setLoaderDone((prev) => {
        if (prev) return prev;
        try {
          sessionStorage.setItem('luna_loader_done', '1');
        } catch {
          /* silent */
        }
        window.dispatchEvent(new CustomEvent('luna:loader-done'));
        return true;
      });
    }, 13_000);
    return () => {
      window.clearTimeout(forceAppVisible);
      window.clearTimeout(forceLoaderDone);
    };
  }, []);

  return (
    <>
      <motion.div
        initial={false}
        animate={{ opacity: appVisible ? 1 : 0 }}
        transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
        style={{ width: '100%', minHeight: '100vh' }}
      >
        <AuthProvider>
          <App />
        </AuthProvider>
      </motion.div>
      {!loaderDone && (
        <LoaderScreen
          onDone={() => {
            setLoaderDone(true);
            // Маркер для компонентов, монтируемых ПОСЛЕ лоадера,
            // чтобы не прогалопировать свой entry-burst во время
            // того как лоадер накрывает их сверху.
            try {
              sessionStorage.setItem('luna_loader_done', '1');
            } catch {
              /* silent */
            }
            window.dispatchEvent(new CustomEvent('luna:loader-done'));
          }}
        />
      )}
    </>
  );
}

/**
 * Platform detection: на iOS backdrop-filter работает в полную силу
 * и даёт красивое размытие. На Android/desktop Telegram WebView
 * либо игнорирует backdrop-filter на дочернем оверлее, либо
 * рендерит без блюра — в итоге прозрачный фон нижней панели
 * показывает контент насквозь. Ставим класс `platform-non-ios`,
 * чтобы CSS мог подменить прозрачное размытие на плотную заливку.
 */
(() => {
  const tgPlatform = (window as any).Telegram?.WebApp?.platform as
    | string
    | undefined;
  const ua = navigator.userAgent || '';
  const isIOS =
    tgPlatform === 'ios' ||
    /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
  if (!isIOS) {
    document.documentElement.classList.add('platform-non-ios');
  }
})();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Root />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
